// Copyright The OpenTelemetry Authors
// SPDX-License-Identifier: Apache-2.0

import styled, { keyframes } from 'styled-components';

const scroll = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-50%);
  }
`;

export const Container = styled.div`
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 20px;

  ${({ theme }) => theme.breakpoints.desktop} {
    padding: 0 32px;
  }
`;

export const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
`;

export const Content = styled.div`
  width: 100%;
  margin-top: 32px;

  ${({ theme }) => theme.breakpoints.desktop} {
    margin-top: 56px;
  }
`;

export const HotProducts = styled.div`
  margin-bottom: 48px;
  scroll-margin-top: 96px;

  ${({ theme }) => theme.breakpoints.desktop} {
    margin-bottom: 96px;
  }
`;

export const SectionHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 20px;
`;

export const HotProductsTitle = styled.h1`
  margin: 0;
  font-size: ${({ theme }) => theme.sizes.mLarge};
  font-weight: ${({ theme }) => theme.fonts.semiBold};
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.ink};

  ${({ theme }) => theme.breakpoints.desktop} {
    font-size: 32px;
  }
`;

export const ProductCount = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.inkMuted};
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 10px;
  margin: 0 -20px 24px;
  padding: 2px 20px 8px;
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }

  ${({ theme }) => theme.breakpoints.desktop} {
    flex-wrap: wrap;
    margin: 0 0 28px;
    padding: 0;
    overflow-x: visible;
  }
`;

export const HeadControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const FilterSelectWrap = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

export const FilterSelect = styled.select`
  -webkit-appearance: none;
  appearance: none;
  padding: 8px 32px 8px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.surface};
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.ink};
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.grafanaOrange};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.grafanaOrange};
    outline-offset: 2px;
  }
`;

export const FilterArrow = styled.img.attrs({
  src: '/icons/Chevron.svg',
  alt: '',
})`
  position: absolute;
  right: 14px;
  width: 9px;
  height: 11px;
  opacity: 0.6;
  pointer-events: none;
`;

export const Chip = styled.button<{ $active: boolean }>`
  flex-shrink: 0;
  padding: 8px 18px;
  border-radius: 999px;
  border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.ink : theme.colors.border)};
  background: ${({ theme, $active }) => ($active ? theme.colors.ink : theme.colors.surface)};
  color: ${({ theme, $active }) => ($active ? theme.colors.white : theme.colors.ink)};
  font-size: 14px;
  font-weight: 500;
  text-transform: capitalize;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: ${({ theme, $active }) => ($active ? theme.colors.ink : theme.colors.grafanaOrange)};
  }
`;

export const Marquee = styled.div`
  overflow: hidden;
  background: ${({ theme }) => theme.colors.ink};
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding: 12px 0;
`;

export const MarqueeTrack = styled.div`
  display: flex;
  gap: 48px;
  width: max-content;
  animation: ${scroll} 28s linear infinite;

  &:hover {
    animation-play-state: paused;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const MarqueeItem = styled.span`
  display: flex;
  align-items: center;
  gap: 48px;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grafanaOrangeLight};

  &::after {
    content: '✦';
    color: rgba(255, 255, 255, 0.35);
  }
`;

export const LogoStrip = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surfaceMuted};
`;

export const LogoStripInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  max-width: 1240px;
  margin: 0 auto;
  padding: 22px 20px;

  ${({ theme }) => theme.breakpoints.desktop} {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: center;
    gap: 24px 52px;
    padding: 30px 20px;
  }
`;

export const LogoStripLabel = styled.span`
  align-self: center;
  padding: 5px 12px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.ink};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.white};
`;

export const LogoRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: center;
  gap: 10px 22px;

  ${({ theme }) => theme.breakpoints.desktop} {
    display: contents;
  }
`;

export const LogoWordmark = styled.span<{ $style: 'serif' | 'mono' | 'wide' | 'italic' | 'black' }>`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.ink};
  opacity: 0.85;
  white-space: nowrap;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 1;
  }

  ${({ $style }) =>
    ($style === 'serif' && 'font-family: Georgia, serif; font-weight: 700;') ||
    ($style === 'mono' && 'font-family: Menlo, monospace; font-weight: 700; font-size: 13px;') ||
    ($style === 'wide' && 'font-weight: 800; letter-spacing: 0.18em; text-transform: uppercase; font-size: 12px;') ||
    ($style === 'italic' && 'font-style: italic; font-weight: 700;') ||
    'font-weight: 900; letter-spacing: -0.02em;'};

  ${({ theme }) => theme.breakpoints.desktop} {
    font-size: 21px;

    ${({ $style }) =>
      ($style === 'mono' && 'font-size: 18px;') ||
      ($style === 'wide' && 'font-size: 17px;') ||
      ''};
  }
`;

export const Benefits = styled.section`
  background: ${({ theme }) => theme.colors.surfaceMuted};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

export const BenefitsHead = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  padding: 56px 20px 0;
  text-align: center;

  ${({ theme }) => theme.breakpoints.desktop} {
    padding: 72px 32px 0;
  }
`;

export const BenefitsEyebrow = styled.p`
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grafanaOrange};
`;

export const BenefitsTitle = styled.h2`
  margin: 0;
  font-size: clamp(26px, 4vw, 36px);
  font-weight: 800;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.ink};
`;

export const BenefitsInner = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  max-width: 1240px;
  margin: 0 auto;
  padding: 32px 20px 56px;

  ${({ theme }) => theme.breakpoints.desktop} {
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
    padding: 40px 32px 72px;
  }
`;

export const BenefitIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  margin-bottom: 16px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(244, 104, 0, 0.14) 0%, rgba(255, 136, 51, 0.22) 100%);
  border: 1px solid rgba(244, 104, 0, 0.25);
  transition: transform 0.2s ease, background 0.2s ease;
`;

export const BenefitCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 16px;
  padding: 26px 24px;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(244, 104, 0, 0.4);
    box-shadow: 0 14px 36px rgba(17, 18, 23, 0.1);

    ${BenefitIcon} {
      background: linear-gradient(135deg, rgba(244, 104, 0, 0.22) 0%, rgba(255, 136, 51, 0.34) 100%);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;

      ${BenefitIcon} {
        transform: none;
      }
    }
  }

  svg {
    stroke: ${({ theme }) => theme.colors.grafanaOrange};
  }

  h3 {
    margin: 0 0 6px;
    font-size: 16px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.ink};
  }

  p {
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
    color: ${({ theme }) => theme.colors.inkMuted};
  }
`;

export const Countdown = styled.section`
  background:
    radial-gradient(1.5px 1.5px at 10% 25%, rgba(255, 255, 255, 0.8), transparent),
    radial-gradient(1px 1px at 24% 70%, rgba(255, 255, 255, 0.5), transparent),
    radial-gradient(1.5px 1.5px at 38% 18%, rgba(255, 255, 255, 0.6), transparent),
    radial-gradient(1px 1px at 52% 60%, rgba(255, 255, 255, 0.45), transparent),
    radial-gradient(1.5px 1.5px at 67% 30%, rgba(255, 255, 255, 0.7), transparent),
    radial-gradient(1px 1px at 80% 75%, rgba(255, 255, 255, 0.5), transparent),
    radial-gradient(1.5px 1.5px at 92% 20%, rgba(255, 255, 255, 0.65), transparent),
    radial-gradient(ellipse 70% 90% at 50% 115%, rgba(244, 104, 0, 0.35), transparent),
    linear-gradient(180deg, #0b0d12 0%, #14161f 100%);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

export const CountdownInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  max-width: 1240px;
  margin: 0 auto;
  padding: 56px 20px;
  text-align: center;

  ${({ theme }) => theme.breakpoints.desktop} {
    padding: 64px 32px;
  }
`;

export const CountdownEyebrow = styled.p`
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grafanaOrangeLight};
`;

export const CountdownLabel = styled.p`
  margin: 0;
  font-size: clamp(22px, 3.4vw, 32px);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: ${({ theme }) => theme.colors.white};

  span {
    color: ${({ theme }) => theme.colors.grafanaOrangeLight};
  }
`;

export const CountdownClock = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 6px 0 4px;
`;

export const CountdownUnit = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 84px;
  padding: 14px 12px 10px;
  border: 1px solid rgba(244, 104, 0, 0.45);
  border-radius: 14px;
  background: rgba(244, 104, 0, 0.08);
  box-shadow: 0 0 32px rgba(244, 104, 0, 0.15);

  strong {
    font-size: 38px;
    font-weight: 800;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    color: ${({ theme }) => theme.colors.white};
  }

  span {
    margin-top: 6px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.grafanaOrangeLight};
  }
`;

export const CountdownSeparator = styled.span`
  font-size: 30px;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.35);
`;

export const Showcase = styled.section`
  max-width: 1240px;
  margin: 0 auto;
  padding: 48px 20px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  align-items: center;

  ${({ theme }) => theme.breakpoints.desktop} {
    grid-template-columns: 1fr 1fr;
    gap: 64px;
    padding: 80px 32px;
  }
`;

export const ShowcaseImage = styled.div`
  position: relative;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 20px;
  background: ${({ theme }) => theme.colors.white};
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    aspect-ratio: 1 / 1;
    object-fit: contain;
  }
`;

export const ShowcaseTag = styled.div`
  position: absolute;
  bottom: 18px;
  left: 18px;
  padding: 8px 16px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.ink};
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.white};

  span {
    color: ${({ theme }) => theme.colors.grafanaOrangeLight};
  }
`;

export const ShowcaseEyebrow = styled.p`
  margin: 0 0 12px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grafanaOrange};
`;

export const ShowcaseTitle = styled.h2`
  margin: 0 0 16px;
  font-size: clamp(28px, 4vw, 40px);
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.ink};
`;

export const ShowcaseCopy = styled.p`
  margin: 0 0 20px;
  font-size: 16px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.inkMuted};
`;

export const ShowcaseList = styled.ul`
  margin: 0 0 28px;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
    font-size: 15px;
    color: ${({ theme }) => theme.colors.ink};

    img {
      width: 15px;
      height: 15px;
    }
  }
`;

export const Comparison = styled.section`
  background:
    radial-gradient(1.5px 1.5px at 8% 20%, rgba(255, 255, 255, 0.7), transparent),
    radial-gradient(1px 1px at 18% 65%, rgba(255, 255, 255, 0.45), transparent),
    radial-gradient(1.5px 1.5px at 30% 12%, rgba(255, 255, 255, 0.55), transparent),
    radial-gradient(1px 1px at 43% 80%, rgba(255, 255, 255, 0.4), transparent),
    radial-gradient(1.5px 1.5px at 58% 8%, rgba(255, 255, 255, 0.6), transparent),
    radial-gradient(1px 1px at 70% 72%, rgba(255, 255, 255, 0.45), transparent),
    radial-gradient(1.5px 1.5px at 82% 18%, rgba(255, 255, 255, 0.65), transparent),
    radial-gradient(1px 1px at 93% 60%, rgba(255, 255, 255, 0.5), transparent),
    radial-gradient(1px 1px at 50% 40%, rgba(255, 255, 255, 0.35), transparent),
    ${({ theme }) => theme.colors.ink};
`;

export const ComparisonInner = styled.div`
  max-width: 860px;
  margin: 0 auto;
  padding: 56px 20px;

  ${({ theme }) => theme.breakpoints.desktop} {
    padding: 80px 32px;
  }
`;

export const ComparisonTitle = styled.h2`
  margin: 0 0 32px;
  font-size: clamp(26px, 4vw, 36px);
  font-weight: 800;
  letter-spacing: -0.02em;
  text-align: center;
  color: ${({ theme }) => theme.colors.white};

  span {
    color: ${({ theme }) => theme.colors.grafanaOrangeLight};
  }
`;

export const ComparisonTable = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 16px;
  overflow: hidden;
`;

export const ComparisonRow = styled.div<{ $head?: boolean }>`
  display: grid;
  grid-template-columns: 1.6fr 1fr 1fr;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: ${({ $head }) => ($head ? 'rgba(255, 255, 255, 0.06)' : 'transparent')};

  &:last-child {
    border-bottom: none;
  }

  ${({ theme }) => theme.breakpoints.desktop} {
    padding: 16px 24px;
  }

  p {
    margin: 0;
    font-size: 14px;
    font-weight: ${({ $head }) => ($head ? 700 : 500)};
    color: ${({ $head }) => ($head ? '#ffffff' : 'rgba(255, 255, 255, 0.85)')};
  }

  p:nth-child(2),
  p:nth-child(3) {
    text-align: center;
  }

  p:nth-child(2) {
    color: ${({ theme, $head }) => ($head ? theme.colors.grafanaOrangeLight : '#ffffff')};
  }

  p:nth-child(3) {
    color: rgba(255, 255, 255, 0.45);
  }
`;

export const Testimonials = styled.section`
  background:
    radial-gradient(1.5px 1.5px at 8% 22%, rgba(255, 255, 255, 0.7), transparent),
    radial-gradient(1px 1px at 20% 70%, rgba(255, 255, 255, 0.45), transparent),
    radial-gradient(1.5px 1.5px at 34% 14%, rgba(255, 255, 255, 0.55), transparent),
    radial-gradient(1px 1px at 47% 78%, rgba(255, 255, 255, 0.4), transparent),
    radial-gradient(1.5px 1.5px at 61% 10%, rgba(255, 255, 255, 0.6), transparent),
    radial-gradient(1px 1px at 74% 66%, rgba(255, 255, 255, 0.45), transparent),
    radial-gradient(1.5px 1.5px at 88% 24%, rgba(255, 255, 255, 0.65), transparent),
    radial-gradient(ellipse 80% 60% at 50% 118%, rgba(244, 104, 0, 0.22), transparent),
    linear-gradient(180deg, #0b0d12 0%, #14161f 100%);
`;

export const TestimonialsInner = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  padding: 56px 20px 64px;

  ${({ theme }) => theme.breakpoints.desktop} {
    padding: 80px 32px 88px;
  }
`;

export const TestimonialsTitle = styled.h2`
  margin: 0 0 8px;
  font-size: clamp(26px, 4vw, 36px);
  font-weight: 800;
  letter-spacing: -0.02em;
  text-align: center;
  color: ${({ theme }) => theme.colors.white};
`;

export const TestimonialsSub = styled.p`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 0 32px;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.7);

  span {
    color: ${({ theme }) => theme.colors.grafanaOrangeLight};
    letter-spacing: 2px;
  }
`;

export const TestimonialGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  ${({ theme }) => theme.breakpoints.desktop} {
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }
`;

export const TestimonialCard = styled.figure`
  position: relative;
  margin: 0;
  padding: 24px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: 0 12px 40px rgba(11, 13, 18, 0.45);

  &::before {
    content: '\\201C';
    position: absolute;
    top: 10px;
    right: 20px;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 72px;
    font-weight: 700;
    line-height: 1;
    color: rgba(244, 104, 0, 0.25);
  }

  blockquote {
    margin: 14px 0 14px;
    font-size: 15px;
    line-height: 1.7;
    color: ${({ theme }) => theme.colors.ink};
  }
`;

export const TestimonialHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  img {
    width: 46px;
    height: 46px;
    border-radius: 50%;
    border: 2px solid ${({ theme }) => theme.colors.border};
  }

  figcaption {
    font-size: 13px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.inkMuted};

    strong {
      display: block;
      font-size: 14px;
      font-weight: 700;
      color: ${({ theme }) => theme.colors.ink};
    }
  }
`;

export const VerifiedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  padding: 4px 10px;
  border-radius: 999px;
  background: #e7f5ec;
  font-size: 11px;
  font-weight: 700;
  color: #1a7f37;
  white-space: nowrap;

  svg {
    stroke: #1a7f37;
  }
`;

export const TestimonialStars = styled.span`
  font-size: 14px;
  letter-spacing: 2px;
  color: ${({ theme }) => theme.colors.grafanaOrange};
`;

export const TestimonialPurchase = styled.p`
  margin: 0;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.inkMuted};

  strong {
    color: ${({ theme }) => theme.colors.ink};
    font-weight: 600;
  }
`;

export const Faq = styled.section`
  max-width: 820px;
  margin: 0 auto;
  padding: 48px 20px 64px;
  scroll-margin-top: 96px;

  ${({ theme }) => theme.breakpoints.desktop} {
    padding: 80px 32px 96px;
  }
`;

export const FaqTitle = styled.h2`
  margin: 0 0 24px;
  font-size: ${({ theme }) => theme.sizes.mLarge};
  font-weight: ${({ theme }) => theme.fonts.semiBold};
  letter-spacing: -0.02em;
  text-align: center;
  color: ${({ theme }) => theme.colors.ink};

  ${({ theme }) => theme.breakpoints.desktop} {
    font-size: 32px;
  }
`;

export const FaqItem = styled.details`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.surface};
  margin-bottom: 10px;
  overflow: hidden;

  &[open] summary::after {
    transform: rotate(45deg);
  }
`;

export const FaqQuestion = styled.summary`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 20px;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.ink};
  cursor: pointer;
  list-style: none;

  &::-webkit-details-marker {
    display: none;
  }

  &::after {
    content: '+';
    flex-shrink: 0;
    font-size: 20px;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.grafanaOrange};
    transition: transform 0.15s ease;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceMuted};
  }
`;

export const FaqAnswer = styled.p`
  margin: 0;
  padding: 0 20px 18px;
  font-size: 14px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.inkMuted};
`;

export const Home = styled.div`
  @media (max-width: 992px) {
    ${Content} {
      width: 100%;
    }
  }
`;
